import { Container, Typography } from '@cleartrip/ct-design-components'
import React from 'react'

import { TIME_OF_DAY_ICON } from '@/assets/icons/TimeOfDay'
import { DEPARTURE_TIME_MAPPING_OBJ } from '@/constants/Srp'

type TimeSlot = keyof typeof DEPARTURE_TIME_MAPPING_OBJ

type TimingCardsProps = {
  /** Heading above the grid, e.g. `Take off from Bangalore`. */
  heading: string
  options: TimeSlot[]
  selectedValues: TimeSlot[]
  onToggle: (option: TimeSlot) => void
}

/**
 * Two fixed columns rather than `1fr`, so a row holding a single card keeps the
 * card's width instead of stretching it across the grid.
 */
const CARDS_ROW = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, calc((100% - 8px) / 2))',
  rowGap: '8px',
  columnGap: '8px',
} as const

const CARD = { minHeight: '79px', padding: '10px' } as const
const CARD_SELECTED = { ...CARD, boxShadow: 'inset 0 0 0 0.5px #1a1a1a' } as const
const CARD_BASE_CLASS = 'flex flex-column flex-center flex-middle flex-rg-1 bs-border br-12 ba-solid c-pointer'
const CARD_CLASS = `${CARD_BASE_CLASS} bc-neutral-500 bg-neutral-100`
const CARD_CLASS_SELECTED = `${CARD_BASE_CLASS} bc-neutral-900 bg-neutral-300`
// Both variants stay mounted (stacked) so selecting only flips opacity — no image load, no flicker.
const ICON_BOX = { position: 'relative', width: 24, height: 24 } as const
const ICON_LAYER = { position: 'absolute', inset: 0, lineHeight: 0 } as const
const ICON_SHOWN = { ...ICON_LAYER, opacity: 1 } as const
const ICON_HIDDEN = { ...ICON_LAYER, opacity: 0 } as const

const TimingCards = ({ heading, onToggle, options, selectedValues }: TimingCardsProps) => (
  <Container className='flex flex-column'>
    {heading && (
      <Typography className='mb-2' color='heading' variant='B3'>
        {heading}
      </Typography>
    )}
    <Container css={CARDS_ROW}>
      {options?.map(option => {
        const { label, subLabel } = DEPARTURE_TIME_MAPPING_OBJ[option] || {}
        const icon = TIME_OF_DAY_ICON[option]
        const checked = selectedValues.includes(option)
        const cardClassName = checked ? CARD_CLASS_SELECTED : CARD_CLASS
        const outlineCss = checked ? ICON_HIDDEN : ICON_SHOWN
        const filledCss = checked ? ICON_SHOWN : ICON_HIDDEN

        return (
          <Container
            key={option}
            className={cardClassName}
            css={checked ? CARD_SELECTED : CARD}
            onClick={() => onToggle(option)}
          >
            {icon && (
              <Container css={ICON_BOX}>
                <Container css={outlineCss}>
                  <icon.outline />
                </Container>
                <Container css={filledCss}>
                  <icon.filled />
                </Container>
              </Container>
            )}
            <Typography color='heading' variant='L3'>
              {label}
            </Typography>
            <Typography color='subheading' variant='B4' css={{ fontWeight: '400' }}>
              {subLabel}
            </Typography>
          </Container>
        )
      })}
    </Container>
  </Container>
)

export default TimingCards
